import React from 'react';
import { Container, Box, Typography, Link } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeModeContext';

const TermsOfService: React.FC = () => {
  const { mode } = useThemeMode();

  return (
    <Box sx={{
      minHeight: '100vh',
      background: mode === 'dark'
        ? 'linear-gradient(180deg, #0f172a 0%, #151b26 25%, #1a2332 50%, #1c2838 75%, #1e293b 100%)'
        : 'linear-gradient(180deg, #FAFBFC 0%, #FAFAFC 25%, #F9FAFB 50%, #F9FAFC 75%, #F8FAFC 100%)',
      pt: { xs: 8, md: 12 },
      pb: { xs: 6, md: 10 },
    }}>
      <Container maxWidth="md">
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 700,
            color: mode === 'dark' ? '#ffffff' : '#0f172a',
            mb: 4,
          }}
        >
          Terms of Service
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: mode === 'dark' ? 'rgba(255,255,255,0.6)' : '#64748b',
            mb: 4,
          }}
        >
          Last updated: {new Date().toLocaleDateString()} (Enterprise Terms)
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              1. Acceptance of Terms
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              These Terms of Service ("Terms") govern your access to and use of the CoreSupport platform, including our web applications, APIs, AI agents, integrations, and any related services (collectively, the "Services"). By creating an account, accessing, or using the Services on behalf of yourself or an organization, you ("Customer", "you") agree to be bound by these Terms. If you are entering into these Terms on behalf of an entity, you represent that you have the authority to bind that entity. If you do not agree to these Terms, you may not access or use the Services.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              2. Description of Services
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              CoreSupport provides AI-powered enterprise support infrastructure, including but not limited to: dedicated AI agents, automated ticket management and routing, knowledge base ingestion and retrieval, multi-channel conversational interfaces, analytics dashboards, and integrations with third-party tools (including, where applicable, AWS Bedrock, AWS OpenSearch, AWS S3, DynamoDB, SES, and related AWS services). Services are provisioned on a per-tenant basis with logical and, where applicable, physical isolation between customers.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              3. Account Registration and Customer Responsibilities
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              You must provide accurate, current, and complete information when creating an account and keep such information updated. You are responsible for all activities that occur under your accounts and for maintaining the confidentiality of your credentials.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              Without limiting the foregoing, you are responsible for:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569' }}>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Maintaining the accuracy and security of your account information and authentication methods.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Ensuring that all end users, agents, and administrators under your organization comply with these Terms and any applicable policies.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Using the Services only in compliance with all applicable laws and regulations, including data protection, privacy, export control, and industry-specific rules.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Configuring role-based access controls and permissions appropriate to your organization.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Reviewing AI outputs before relying on them for critical decisions; the Services may generate content that is probabilistic and not guaranteed to be accurate.</Typography></li>
            </Box>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              4. Acceptable Use and Prohibited Activities
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mb: 2,
              }}
            >
              You agree not to, and not to allow any third party to, directly or indirectly use the Services to:
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569' }}>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Use the Services for any unlawful, harmful, fraudulent, infringing, or offensive purpose, or to encourage or facilitate such activity.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Upload or transmit malicious code, malware, or any content that could damage or interfere with systems, data, or networks.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Attempt to gain unauthorized access to the Services, underlying infrastructure, or other customers' environments.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Use the Services to process highly sensitive information (such as payment card data, government-classified information, or data subject to ITAR/defense regulations) unless expressly agreed in writing.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Use the Services in a way that violates third-party rights, including intellectual property, privacy, or contractual rights.</Typography></li>
              <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Benchmark, reverse engineer, or attempt to derive source code or underlying models, except to the extent permitted by law.</Typography></li>
            </Box>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              5. Intellectual Property and Customer Content
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              CoreSupport and its licensors own all right, title, and interest in and to the Services, including all software, models, user interfaces, designs, logos, and documentation, subject only to the limited rights expressly granted to you in these Terms.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mt: 2,
              }}
            >
              You retain all rights in and to the data, documents, tickets, conversations, and other content you or your end users submit to the Services ("Customer Content"). You grant CoreSupport and its subprocessors a worldwide, non-exclusive, royalty-free license to host, process, store, transmit, and use Customer Content solely as necessary to provide, maintain, secure, and improve the Services, including for training and optimizing organization-specific AI agents where configured by you.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              6. Payment, Subscription, and Taxes
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              Subscription fees and any applicable usage-based charges are billed in advance on a monthly or annual basis, as specified in an order form or online subscription flow. Unless otherwise stated, fees are non-refundable and exclude taxes, levies, or duties. You are responsible for all applicable taxes other than our income taxes. We may update pricing upon renewal or with at least thirty (30) days' notice to existing customers.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              7. Service Availability, Security, and Maintenance
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              We operate the Services using modern cloud infrastructure and industry-standard security practices, including network segregation, access controls, logging, and encryption at rest and in transit where supported by the underlying cloud providers. While we strive for high availability and resilience, the Services may be subject to scheduled maintenance, emergency maintenance, or interruptions beyond our reasonable control. Unless otherwise agreed in a separate Service Level Agreement (SLA), we do not guarantee uninterrupted or error-free operation.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              8. Disclaimers and Limitation of Liability
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT AI-GENERATED OUTPUTS WILL BE ACCURATE, COMPLETE, OR SUITABLE FOR ANY PARTICULAR USE CASE.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mt: 2,
              }}
            >
              To the maximum extent permitted by law, CoreSupport and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or loss of profits, revenues, data, or goodwill, arising out of or in connection with the use of or inability to use the Services, even if advised of the possibility of such damages. Our aggregate liability for all claims arising under these Terms shall not exceed the amounts paid by you to us for the Services in the twelve (12) months preceding the event giving rise to the claim.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              9. Term, Suspension, and Termination
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              Either party may terminate the Services for convenience at the end of a subscription term or as otherwise specified in an order form. We may suspend or restrict access to the Services immediately if you violate these Terms, if your account is overdue, or if required by law or to protect the security, integrity, or rights of any party.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mt: 2,
              }}
            >
              Upon termination or expiration, your access to the Services will cease. We will handle Customer Content in accordance with our Privacy Policy and any applicable data processing or retention terms. You remain responsible for all fees incurred prior to termination.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: mode === 'dark' ? '#ffffff' : '#0f172a',
                mb: 2,
              }}
            >
              10. Data Protection, Compliance, and Contact Information
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
              }}
            >
              We process personal data as described in our Privacy Policy and, where required, under a separate Data Processing Addendum (DPA) that incorporates appropriate data protection commitments. If you are subject to specific regulatory frameworks (such as GDPR, CCPA, HIPAA, or financial services regulations), additional terms may apply and must be agreed in writing.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#475569',
                lineHeight: 1.8,
                mt: 2,
              }}
            >
              For questions about these Terms of Service, data protection, or compliance, please contact us at:{' '}
              <Link href="mailto:legal@coresupportai.com" sx={{ color: '#6366f1' }}>
                legal@coresupportai.com
              </Link>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsOfService;

